import React, { useRef, useEffect } from 'react';
// Fix: Use namespace import for react-router-dom to avoid missing exported member errors
import * as ReactRouterDOM from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../hooks/useAuth';
import { formatCurrency } from '../../utils/helpers';

const { useNavigate } = ReactRouterDOM as any;

interface CartDropdownProps {
  isOpen: boolean;
  onClose: () => void;
}

const CartDropdown: React.FC<CartDropdownProps> = ({ isOpen, onClose }) => {
  const { cartItems, removeFromCart, totalAmount } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleViewCart = () => {
      onClose();
      navigate('/cart');
  };

  const handleCheckout = () => {
      onClose();
      if (user) {
          navigate('/dashboard/checkout');
      } else {
          navigate('/login');
      }
  };

  return (
    <div ref={dropdownRef} className="absolute top-full mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-xl ring-1 ring-black ring-opacity-5 z-50 ltr:right-0 rtl:left-0 origin-top-right rtl:origin-top-left">
      <div className="p-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cart.title')}</h3>
        
        {cartItems.length === 0 ? (
            <div className="text-center py-6">
                <p className="text-gray-500 dark:text-gray-400 text-sm">{t('cart.empty_title')}</p>
            </div>
        ) : (
            <>
                <div className="max-h-60 overflow-y-auto space-y-3 mb-4">
                    {cartItems.map((item, idx) => (
                        <div key={`${item.produit_id}-${idx}`} className="flex justify-between items-start border-b border-gray-100 dark:border-gray-700 pb-2 last:border-0">
                            <div className="flex-1 min-w-0 mr-2 rtl:mr-0 rtl:ml-2">
                                <p className="text-sm font-medium text-gray-800 dark:text-white truncate">
                                    {t(`product_names.${item.nom_produit}`, { defaultValue: item.nom_produit })}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 ltr:font-sans rtl:font-sans">
                                    {item.quantite} x {formatCurrency(item.prix_unitaire)}
                                </p>
                            </div>
                            <button 
                                onClick={() => removeFromCart(item.produit_id, item.configuration)}
                                className="text-red-500 hover:text-red-700 p-1"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414-1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    ))}
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mb-4">
                    <div className="flex justify-between items-center font-bold text-gray-900 dark:text-white">
                        <span>{t('cart.total')}</span>
                        <span className="ltr:font-sans rtl:font-sans">{formatCurrency(totalAmount)}</span>
                    </div>
                </div>

                <div className="space-y-2">
                    <button 
                        onClick={handleViewCart}
                        className="w-full py-2 px-4 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-white rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
                    >
                        {t('cart.title')}
                    </button>
                    <button 
                        onClick={handleCheckout}
                        className="w-full py-2 px-4 bg-primary-500 text-white rounded-md hover:bg-primary-400 transition-colors text-sm font-medium"
                    >
                        {t('cart.checkout_btn')}
                    </button>
                </div>
            </>
        )}
      </div>
    </div>
  );
};

export default CartDropdown;